import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Grid, IconButton } from "@mui/material";
import { NavLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";

import Logo from "assets/images/Logo.svg";
import { appRoutes, externalRoutes } from "routes";
import UserPresenter from "presenters/UserPresenter";
import MegaMenuItemPresenter from "presenters/MegaMenuItemPresenter";
import { headerSelector } from "tests/selectors/index.js";
import { useRouter, useUsers, useAlgolia } from "hooks";
import { AUTH_LINKS } from "enums";

import MenuDrawer from "../MenuDrawer";
import Avatar from "../AccountMenu/components/Avatar/Avatar";

import MenuItem from "./components/MenuItem/MenuItem";
import { StyledGrid, StyledMenuItem, StyledButton, Message, Action, StyledMenuItemButton } from "./styles";

const MobileHeader = ({ menuItems }) => {
  const theme = useTheme();
  const { currentUser, signOut } = useUsers();
  const { clearAllFiltersByPath } = useAlgolia();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);

  const [isAccountOpen, setAccountOpen] = useState(false);
  const openAccount = () => setAccountOpen(true);
  const closeAccount = () => setAccountOpen(false);
  const { location } = useRouter();

  const mainMenuLinks = useMemo(
    () => menuItems?.map((item) => MegaMenuItemPresenter.linkTo(item.menu)).filter(Boolean) || [],
    [menuItems],
  );

  const renderCommonLinks = (link) => {
    const { to: pathname, name, id } = link;
    const key = `hamburger-${id}`;
    const isLoginLink = name === "Log In";
    const toParams = { pathname };

    if (isLoginLink) toParams.state = { referrer: location.pathname };

    return (
      <StyledMenuItem id={key} onClick={() => closeAccount()} to={toParams} key={key} component={NavLink}>
        {name}
      </StyledMenuItem>
    );
  };

  return (
    <StyledGrid container spacing={3}>
      <Grid item xs>
        <Action
          id="header-hamburger-mobile"
          data-testid={headerSelector.buttons.hamburguer}
          alt="Menu"
          onClick={openMenu}
        >
          <MenuIcon />
        </Action>

        <MenuDrawer anchor="left" open={isMenuOpen} onClose={closeMenu}>
          {menuItems &&
            menuItems.map((item) => (
              <MenuItem
                key={item.menu.name}
                menuItem={item.menu}
                mainMenuLinks={mainMenuLinks}
                onClick={() => clearAllFiltersByPath({ path: MegaMenuItemPresenter.linkTo(item.menu) })}
                closeMenu={closeMenu}
              />
            ))}
        </MenuDrawer>

        <IconButton
          id="header-search-mobile"
          alt="Search"
          to={appRoutes.searchPath()}
          component={NavLink}
          onClick={() => clearAllFiltersByPath({ path: appRoutes.searchPath() })}
        >
          <SearchIcon />
        </IconButton>
      </Grid>

      <Grid item xs={6} style={{ textAlign: "center" }}>
        <IconButton id="header-logo-mobile" alt="JibJab" to={appRoutes.rootPath()} component={NavLink}>
          <img src={Logo} alt="JibJab" width="120px" height="36px" />
        </IconButton>
      </Grid>

      <Grid item xs style={{ textAlign: "right" }}>
        <Avatar onClick={openAccount} />

        <MenuDrawer anchor="right" open={isAccountOpen} onClose={closeAccount}>
          {!UserPresenter.isRegistered(currentUser) && (
            <StyledMenuItem
              id="header-login-mobile"
              to={appRoutes.loginPath()}
              component={NavLink}
              onClick={closeAccount}
            >
              Log In
            </StyledMenuItem>
          )}

          {UserPresenter.isRegistered(currentUser) && AUTH_LINKS.map((link) => renderCommonLinks(link))}

          <StyledMenuItem id="header-help-mobile" href={externalRoutes.helpPath()} onClick={closeAccount}>
            Help Center
          </StyledMenuItem>

          {UserPresenter.isRegistered(currentUser) && (
            <StyledMenuItem
              id="header-logout-mobile"
              to={appRoutes.rootPath()}
              component={NavLink}
              onClick={() => signOut()}
            >
              Log Out
            </StyledMenuItem>
          )}

          {UserPresenter.isRegistered(currentUser) && !UserPresenter.isPaid(currentUser) && (
            <Box>
              <StyledMenuItemButton
                id="header-upgrade-mobile"
                to={appRoutes.paymentPath()}
                component={NavLink}
                variant="contained"
                color="primary"
                onClick={closeAccount}
              >
                Upgrade
              </StyledMenuItemButton>
            </Box>
          )}

          {!UserPresenter.isRegistered(currentUser) && (
            <>
              <Message color={theme.palette.text.light}>
                Haven’t join the fun? Create a free account to save your faces and Ecards!
              </Message>

              <Box>
                <StyledButton
                  id="header-sign-up-mobile"
                  to={appRoutes.signUpPath()}
                  component={NavLink}
                  variant="contained"
                  color="primary"
                  onClick={closeAccount}
                >
                  Sign Up
                </StyledButton>
              </Box>
            </>
          )}
        </MenuDrawer>
      </Grid>
    </StyledGrid>
  );
};

MobileHeader.propTypes = {
  menuItems: PropTypes.arrayOf(MegaMenuItemPresenter.shape()),
};

export default MobileHeader;
